import React from "react";
import cx from "classnames";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

import { Ellipsis } from "@common/components/Ellipsis";
import { FieldPickerField } from "../../fields.types";
import { getTestAttributes } from "@common/utils/tests";
import { DATASHEETS_MODULE_NAME } from "@modules/datasheets/constants/common";
import { Hint } from "@common/components/hint";
import { trDts } from "@modules/datasheets/utils/i18n";

import "./field.styles.scss";

type FieldProps = {
  dndType: string;
  className?: string;
  field: FieldPickerField;
  isAdded: boolean;
  isSelected: boolean;
  onMouseDown: (params: { item: FieldPickerField; event: React.MouseEvent<HTMLDivElement> }) => void;
  onMouseUp: (params: { item: FieldPickerField; event: React.MouseEvent<HTMLDivElement> }) => void;
};

export const Field: React.FC<FieldProps> = (props) => {
  const { className, field, isSelected, onMouseDown, onMouseUp, dndType, isAdded } = props;

  const [{}, dragRef, dragPreview] = useDrag({
    type: dndType,
    item: () => field,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  React.useEffect(function hideDefaultPreview() {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  const handleMouseDown = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    onMouseDown({ item: field, event });
  }, [field, onMouseDown]);

  const handleMouseUp = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    onMouseUp({ item: field, event });
  }, [field, onMouseUp]);

  const isSystemField = field.original.system;

  return (
    <div
      {...getTestAttributes(`${DATASHEETS_MODULE_NAME.editor}_field-picker`, 'field', 'div', field.original.id)}
      className={cx(
        "DatasheetsEditorFieldsItem", 
        {
          "DatasheetsEditorFieldsItem--selected": isSelected,
          "DatasheetsEditorFieldsItem--system": isSystemField,
        },
        className,
      )}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      ref={isAdded ? undefined : dragRef}
    >
      <img
        className="DatasheetsEditorFieldsItem__icon"
        src={field.original.imageURL}
        alt={field.name}
      />
      <Ellipsis maxWidth="calc(100% - 32px)" text={field.name} />
    </div>
  );
};
